// Finite state machine for auth lifecycle coordination.
//
// A single state variable and explicit transition table govern the entire auth
// lifecycle. Each transition can trigger a colocated side effect automatically.
//
// Internal states (7): INITIALIZING → HYDRATING → AUTHENTICATED, etc.
// External statuses (3): loading | authenticated | unauthenticated
//
// UI consumers (PrivateRoute, etc.) read getStatus() for the external status.
// Services (LogoutService, LoginService) read getState() when they need to
// distinguish internal states (e.g. SESSION_EXPIRED vs UNAUTHENTICATED).

export const AUTH_STATES = {
  INITIALIZING: "initializing",
  HYDRATING: "hydrating",
  CALLBACK_SETTLING: "callback_settling",
  AUTHENTICATED: "authenticated",
  SESSION_EXPIRED: "session_expired",
  LOGGING_OUT: "logging_out",
  UNAUTHENTICATED: "unauthenticated",
};

export const AUTH_EVENTS = {
  MANAGER_READY: "MANAGER_READY",
  TOKEN_VALID: "TOKEN_VALID",
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
  TOKEN_EXPIRED_WAS_AUTHED: "TOKEN_EXPIRED_WAS_AUTHED",
  CALLBACK_PENDING: "CALLBACK_PENDING",
  CALLBACK_SETTLED: "CALLBACK_SETTLED",
  SETTLE_TIMEOUT: "SETTLE_TIMEOUT",
  HYDRATION_FAILED: "HYDRATION_FAILED",
  LOGOUT_REQUESTED: "LOGOUT_REQUESTED",
  LOGOUT_COMPLETE: "LOGOUT_COMPLETE",
  CROSS_TAB_LOGOUT: "CROSS_TAB_LOGOUT",
  RE_EVALUATE: "RE_EVALUATE",
  ERROR: "ERROR",
};

export const EXTERNAL_STATUS = {
  LOADING: "loading",
  AUTHENTICATED: "authenticated",
  UNAUTHENTICATED: "unauthenticated",
};

// Maps internal states to the three statuses consumers need
const STATUS_MAP = {
  [AUTH_STATES.INITIALIZING]: EXTERNAL_STATUS.LOADING,
  [AUTH_STATES.HYDRATING]: EXTERNAL_STATUS.LOADING,
  [AUTH_STATES.CALLBACK_SETTLING]: EXTERNAL_STATUS.LOADING,
  [AUTH_STATES.AUTHENTICATED]: EXTERNAL_STATUS.AUTHENTICATED,
  [AUTH_STATES.SESSION_EXPIRED]: EXTERNAL_STATUS.UNAUTHENTICATED,
  [AUTH_STATES.LOGGING_OUT]: EXTERNAL_STATUS.UNAUTHENTICATED,
  [AUTH_STATES.UNAUTHENTICATED]: EXTERNAL_STATUS.UNAUTHENTICATED,
};

// Transition table: [currentState][event] → { target, effect? }
// Effects are provided at creation time so the machine has no direct
// dependencies on external services.
const buildTransitionTable = (effects = {}) => ({
  [AUTH_STATES.INITIALIZING]: {
    [AUTH_EVENTS.MANAGER_READY]: { target: AUTH_STATES.HYDRATING },
  },
  [AUTH_STATES.HYDRATING]: {
    [AUTH_EVENTS.TOKEN_VALID]: {
      target: AUTH_STATES.AUTHENTICATED,
      effect: effects.onAuthenticated,
    },
    [AUTH_EVENTS.TOKEN_EXPIRED]: { target: AUTH_STATES.UNAUTHENTICATED },
    [AUTH_EVENTS.TOKEN_EXPIRED_WAS_AUTHED]: {
      target: AUTH_STATES.SESSION_EXPIRED,
      effect: effects.onSessionExpired,
    },
    [AUTH_EVENTS.CALLBACK_PENDING]: { target: AUTH_STATES.CALLBACK_SETTLING },
    [AUTH_EVENTS.HYDRATION_FAILED]: { target: AUTH_STATES.UNAUTHENTICATED },
    [AUTH_EVENTS.CROSS_TAB_LOGOUT]: {
      target: AUTH_STATES.UNAUTHENTICATED,
      effect: effects.onCrossTabLogout,
    },
    // Handles the case where the user navigates to /logout while a
    // re-evaluation is in-flight. Without this, LOGOUT_REQUESTED is
    // silently dropped and the pending evaluate() sends
    // TOKEN_EXPIRED_WAS_AUTHED, triggering the session-expired banner
    // on what should be a clean manual logout.
    [AUTH_EVENTS.LOGOUT_REQUESTED]: { target: AUTH_STATES.LOGGING_OUT },
    [AUTH_EVENTS.ERROR]: {
      target: AUTH_STATES.UNAUTHENTICATED,
      effect: effects.onError,
    },
  },
  [AUTH_STATES.CALLBACK_SETTLING]: {
    [AUTH_EVENTS.CALLBACK_SETTLED]: {
      target: AUTH_STATES.AUTHENTICATED,
      effect: effects.onAuthenticated,
    },
    [AUTH_EVENTS.SETTLE_TIMEOUT]: { target: AUTH_STATES.UNAUTHENTICATED },
    [AUTH_EVENTS.TOKEN_VALID]: {
      target: AUTH_STATES.AUTHENTICATED,
      effect: effects.onAuthenticated,
    },
    [AUTH_EVENTS.ERROR]: {
      target: AUTH_STATES.UNAUTHENTICATED,
      effect: effects.onError,
    },
  },
  [AUTH_STATES.AUTHENTICATED]: {
    [AUTH_EVENTS.TOKEN_EXPIRED_WAS_AUTHED]: {
      target: AUTH_STATES.SESSION_EXPIRED,
      effect: effects.onSessionExpired,
    },
    [AUTH_EVENTS.LOGOUT_REQUESTED]: { target: AUTH_STATES.LOGGING_OUT },
    [AUTH_EVENTS.CROSS_TAB_LOGOUT]: {
      target: AUTH_STATES.UNAUTHENTICATED,
      effect: effects.onCrossTabLogout,
    },
    [AUTH_EVENTS.RE_EVALUATE]: { target: AUTH_STATES.HYDRATING },
    [AUTH_EVENTS.ERROR]: {
      target: AUTH_STATES.UNAUTHENTICATED,
      effect: effects.onError,
    },
  },
  [AUTH_STATES.SESSION_EXPIRED]: {
    // Terminal state. signinRedirect reloads the page, creating a fresh machine.
  },
  [AUTH_STATES.LOGGING_OUT]: {
    [AUTH_EVENTS.LOGOUT_COMPLETE]: { target: AUTH_STATES.UNAUTHENTICATED },
  },
  [AUTH_STATES.UNAUTHENTICATED]: {
    [AUTH_EVENTS.RE_EVALUATE]: { target: AUTH_STATES.HYDRATING },
    [AUTH_EVENTS.TOKEN_VALID]: {
      target: AUTH_STATES.AUTHENTICATED,
      effect: effects.onAuthenticated,
    },
  },
});

// Creates a new auth state machine instance.
//
// @param {Object} effects — callbacks fired on specific transitions:
//   onAuthenticated()   — machine entered AUTHENTICATED (run bootstrap side effects)
//   onSessionExpired()  — token expired while user was authenticated (cleanup + banner)
//   onCrossTabLogout()  — another tab cleared auth storage (drop stale in-memory user)
//   onError()           — unrecoverable hydration/evaluation error (clear session)
export const createAuthStateMachine = ({ effects = {} } = {}) => {
  let currentState = AUTH_STATES.INITIALIZING;
  const listeners = new Set();
  const transitionTable = buildTransitionTable(effects);

  const getState = () => currentState;
  const getStatus = () => STATUS_MAP[currentState];

  const send = (event) => {
    const row = transitionTable[currentState]?.[event];
    if (!row) {
      return; // Event not valid for current state — silently ignore
    }

    const prevState = currentState;
    currentState = row.target;

    // Run transition effect
    if (row.effect) {
      try {
        row.effect({ from: prevState, to: currentState, event });
      } catch (error) {
        console.error(
          `Auth transition effect failed: ${prevState} → ${currentState}`,
          error,
        );
      }
    }

    // Notify subscribers with the external status
    const status = STATUS_MAP[currentState];
    listeners.forEach((fn) => fn(status, currentState));
  };

  const subscribe = (fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
  };

  return { getState, getStatus, send, subscribe };
};
